import type React from "react";
import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Snackbar,
	Typography,
	CircularProgress,
} from "@mui/material";
import { Alert } from "@mui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import {
	linkTemplateDrawings,
	unLinkTemplateDrawings,
} from "@/pages/templates/repositories/TemplatesRepositorie";
import { DocumentsCatalogFreshness } from "@/shared/catalogs/components/CatalogFreshnessControls";
import type { Drawing } from "../../../../models/TemplateDrawing";
import { DrawingTable } from "./drawings-tables";

interface Props {
	searchDrawings: Function;
	show: boolean;
	isLoading: boolean;
	onHide: (shouldUpdate: boolean) => void;
	TemplateId: number | null;
	setShowLinkResourcesToAssetDialog: Function;
}

export const DrawingPicker: React.FC<Props> = ({
	searchDrawings,
	show,
	onHide,
	TemplateId,
	isLoading,
	setShowLinkResourcesToAssetDialog,
}) => {
	const {
		state: { listAllDrawings, templateDrawingsList },
		actions: { setTemplateDrawingToAdd, setListAllDrawings },
	} = useTemplatesContext();

	const [selectedDrawings, setSelectedDrawings] = useState<Drawing[]>([]);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [loadingSave, setLoadingSave] = useState(false);

	useEffect(() => {
		if (show) {
			searchDrawings();
		}
	}, [show]);

	useEffect(() => {
		if (show) {
			setSelectedDrawings(templateDrawingsList);
		}
	}, [show, templateDrawingsList]);

	const handleSelectionChanged = (newSelectedDrawings: Drawing[]) => {
		setSelectedDrawings(newSelectedDrawings);
	};

	const handleSave = async () => {
		setLoadingSave(true);
		const drawingsToAdd = selectedDrawings.filter(
			(drawing) =>
				!templateDrawingsList.some(
					(wdDrawing) => wdDrawing.DocumentName === drawing.DocumentName,
				),
		);

		const drawingsToRemove = templateDrawingsList.filter(
			(drawing) =>
				!selectedDrawings.some(
					(sdDrawing) => sdDrawing.DocumentName === drawing.DocumentName,
				),
		);

		if (drawingsToAdd.length > 0) {
			const newDrawings = drawingsToAdd.map((drawing) => ({
				DocumentName: drawing.DocumentName ?? "",
				IsAssetResource: false,
			}));
			setTemplateDrawingToAdd(drawingsToAdd);
			await linkTemplateDrawings(TemplateId as number, newDrawings);
			setLoadingSave(false);

			setShowLinkResourcesToAssetDialog(true);

			setSuccessMessage("Documents added successfully");
		}

		if (drawingsToRemove.length > 0) {
			const drawingsToRemoveFormatted = drawingsToRemove.map((drawing) => ({
				DocumentName: drawing.DocumentName ?? "",
				IsAssetResource: drawing.IsAssetResource,
			}));
			await unLinkTemplateDrawings(
				TemplateId as number,
				drawingsToRemoveFormatted,
			);
			setSuccessMessage("Documents removed successfully");
		}

		if (drawingsToAdd.length > 0 || drawingsToRemove.length > 0) {
			onHide(true);
			setLoadingSave(false);
		} else {
			onHide(false);
			setLoadingSave(false);
		}
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle style={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" style={{ margin: "0", fontWeight: 600 }}>
						DOCUMENTS
					</Typography>
				</DialogTitle>
				<DialogContent>
					<DrawingTable
						allDrawings={listAllDrawings}
						templateDrawings={templateDrawingsList}
						onSelectionChanged={handleSelectionChanged}
						isLoading={isLoading}
					/>
					<DocumentsCatalogFreshness
						onReloaded={setListAllDrawings}
						onError={setError}
					/>
				</DialogContent>
				<DialogActions
					sx={{ padding: "16px 24px 24px", justifyContent: "flex-end", gap: 2 }}
				>
					<Button
						variant="contained"
						color="inherit"
						onClick={() => onHide(false)}
						sx={{ minWidth: 120 }}
					>
						CANCEL
					</Button>
					<Button
						startIcon={loadingSave && <CircularProgress size="1rem" />}
						disabled={loadingSave}
						variant="contained"
						color="primary"
						onClick={handleSave}
						sx={{ minWidth: 120 }}
					>
						SAVE
					</Button>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
